import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from "react-redux";
import { DateTime } from "luxon";
import { Card, Box } from "@mui/material";
import { http } from '../../../../clients/http/http.client';
import WeeklyPeakUsageChart from "./WeeklyPeakUsageChart";
import CartStatisticsView from "./CartStatisticsView";
import Loader from "../../../Inputs/Loader/Loader";
import { HTTP_ENDPOINTS } from "../../../../clients/clients.constants";
import { useLocation } from "react-router-dom";
import ToggleViewButton from "./ToggleViewButton";

const WeeklyPeakUsageChartCard = ({
  siteId,
  fixtures,
  isMonthly,
  setIsMonthly
}) => {
  const [weeklyPeakUsages, setWeeklyPeakUsages] = useState([]);
  const [loading, setLoading] = useState(false);
  const timeZone = useSelector((state) => state.timeZone.timeZone);
  const selectedDate = useSelector((state) => state.peakUsageSelectedDate.selectedDate);
  const location = useLocation();

  const buildQueryParams = ({ siteId, fixtures, locationSearch, date }) => {
    const params = new URLSearchParams();
    if (siteId) params.append('siteId', siteId);
    if (locationSearch.includes('arrc-inventory')) {
      params.delete('siteId');
      params.append('siteId', 1);
    }
    if (fixtures?.length) {
      params.append('equipmentTypeIds', fixtures.join(','));
    }
    if (date) {
      const adjustedDate = DateTime.fromISO(date).minus({ days: 1 }).toFormat('yyyy-MM-dd');
      params.append('date', adjustedDate);
    }
    return params.toString() ? `?${params.toString()}` : '';
  };

  const fixturesKey = useMemo(() => (fixtures ? [...fixtures].sort().join(',') : ''), [fixtures]);

  // Fetch weekly data
  useEffect(() => {
    if (!siteId || !selectedDate || isMonthly) return;
    const fetchWeeklyData = async () => {
      try {
        setLoading(true);
        const qs = buildQueryParams({
          siteId,
          fixtures,
          locationSearch: location.search,
          date: selectedDate,
        });
        const res = await http.client.get(`${HTTP_ENDPOINTS.EQUIPMENT_WEEKLY_PEAK_USAGE}${qs}`);
        setWeeklyPeakUsages(res);
        setLoading(false);
      } catch (e) {
        setLoading(false);
        console.error(e);
      }
    };
    fetchWeeklyData();
  }, [siteId, fixturesKey, selectedDate, isMonthly, location.search, fixtures]);

  const handleToggle = () => {
    setIsMonthly((prev) => !prev);
  };

  const renderContent = () => {
    if (loading) {
      return <Loader sx={{ width: "100%", height: "100%" }} />;
    }
    if (isMonthly) {
      return <CartStatisticsView siteId={siteId} onBackToChart={handleToggle} isMonthly={isMonthly} handleToggle={handleToggle} />;
    }
    return <WeeklyPeakUsageChart weeklyPeakUsages={weeklyPeakUsages} timeZone={timeZone} isMonthly={isMonthly} handleToggle={handleToggle} />;
  };

  return (
    <Card
      sx={{
        p: 2,
        borderRadius: '12px',
        boxShadow: '0px 4px 40px 0px rgba(226, 236, 249, 0.60)',
        backgroundColor: '#fff',
        overflow: 'unset',
        height: isMonthly ? '160px' : '200px',
      }}
    >
      {/* Button moved to children */}
      {renderContent()}
    </Card>
  );
};

export default WeeklyPeakUsageChartCard;